import { ChangeDetectionStrategy, Component, contentChildren, input, model, output } from '@angular/core';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';
import { StepperStepComponent } from './stepper-step.component';
import { STEPPER_PARENT } from './stepper.token';

/** Minimal public shape mirroring `StepperStepComponent` inputs. */
export interface StepperStep {
  label: string;
  hint?: string;
  hasError?: boolean;
}

/**
 * Linear wizard / stepper. Compose with `<app-stepper-step>` children — the stepper
 * builds the visual trail from each step's `label` / `hint` and only shows the active
 * step's projected content.
 *
 * @example
 * <app-stepper [(activeIndex)]="step">
 *   <app-stepper-step label="Profile">…</app-stepper-step>
 *   <app-stepper-step label="Goals">…</app-stepper-step>
 *   <app-stepper-step label="Review">…</app-stepper-step>
 * </app-stepper>
 *
 * @feature shared
 */
@Component({
  selector: 'app-stepper',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  host: { class: 'block' },
  providers: [{ provide: STEPPER_PARENT, useExisting: StepperComponent }],
  template: `
    <ol class="flex flex-col gap-2 md:flex-row md:items-start md:gap-4" aria-label="Progress">
      @for (step of steps(); track $index; let i = $index; let last = $last) {
        <li class="flex items-start gap-3 md:flex-1">
          <div class="flex flex-col items-center">
            <button
              type="button"
              class="flex h-8 w-8 items-center justify-center rounded-full border-2 font-semibold text-xs transition-colors focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
              [class.border-primary]="isActive(i) && !step.hasError()"
              [class.bg-primary]="isActive(i) && !step.hasError()"
              [class.border-success]="isComplete(i) && !step.hasError()"
              [class.bg-success]="isComplete(i) && !step.hasError()"
              [class.border-danger]="step.hasError()"
              [class.bg-danger]="step.hasError()"
              [class.text-white]="(isActive(i) || isComplete(i) || step.hasError()) && true"
              [class.border-outline]="!isActive(i) && !isComplete(i) && !step.hasError()"
              [class.bg-surface]="!isActive(i) && !isComplete(i) && !step.hasError()"
              [class.text-muted]="!isActive(i) && !isComplete(i) && !step.hasError()"
              [attr.aria-current]="isActive(i) ? 'step' : null"
              [attr.aria-label]="step.label()"
              (click)="jumpTo(i)"
            >
              @if (step.hasError()) {
                <app-icon name="close" size="xxs" />
              } @else if (isComplete(i)) {
                <app-icon name="checkmark" size="xxs" />
              } @else {
                {{ i + 1 }}
              }
            </button>
          </div>

          <div class="flex flex-col min-w-0">
            <span
              class="text-sm font-semibold"
              [class.text-body]="isActive(i) || isComplete(i)"
              [class.text-muted]="!isActive(i) && !isComplete(i)"
            >
              {{ step.label() }}
            </span>
            @if (step.hint()) {
              <span class="text-xs text-muted">{{ step.hint() }}</span>
            }
          </div>

          @if (!last) {
            <div class="hidden md:block flex-1 h-px bg-outline-low mt-4"></div>
          }
        </li>
      }
    </ol>

    <div class="mt-6">
      <ng-content />
    </div>
  `,
})
export class StepperComponent {
  /** Projected step definitions. */
  readonly steps = contentChildren(StepperStepComponent);

  /** Two-way bound 0-indexed active step. */
  readonly activeIndex = model<number>(0);

  /** Allow jumping backwards/forwards by clicking a step. @default 'backward' */
  readonly allowJump = input<'none' | 'backward' | 'any'>('backward');

  /** Fires when `next()` is called past the last step. */
  readonly completed = output<void>();

  isActive(index: number): boolean {
    return index === this.activeIndex();
  }

  isComplete(index: number): boolean {
    return index < this.activeIndex();
  }

  jumpTo(index: number): void {
    const mode = this.allowJump();
    if (mode === 'none') return;
    if (mode === 'backward' && index > this.activeIndex()) return;
    this.activeIndex.set(index);
  }

  next(): void {
    const last = this.steps().length - 1;
    if (this.activeIndex() >= last) {
      this.completed.emit();
      return;
    }
    this.activeIndex.update(i => i + 1);
  }

  prev(): void {
    this.activeIndex.update(i => Math.max(0, i - 1));
  }
}
